import React, {useContext, useMemo, ReactElement} from "react";
import {inject, observer} from "mobx-react";
import { RootStore } from 'stores';
import _ from "lodash";
import {
    InputField,
    isTabletPortraitDown,
    ToastContext
} from "@enter/core";
import * as S from "./shared.styles";
import * as SP from "./settings-page.styles";
import {Icon} from "@mdi/react";
import {mdiArrowLeft, mdiPlus, mdiDelete, mdiClipboardArrowUpOutline} from "@mdi/js";
import { DialogContext } from 'services';
import styled from "@emotion/styled/macro";

import {Formik, FormikHelpers, FormikProps, Form, Field} from "formik";
import * as Yup from "yup";
import copy from 'copy-text-to-clipboard';

const ActionsContainer = styled.div`
  padding-top:10px;
`;

export const SingleSignOn: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const { settingsStore } = rootStore;
            
            const hasAdminAccess = rootStore.roleId === 'PROVIDER_ADMIN_ACCESS';
            
            if (!hasAdminAccess) {
                return <></>;
            }

            const onFormSubmit = async (values: FormProps, formik: FormikHelpers<FormProps>) => {
                formik.setSubmitting(true);
                try {
                    const domains: string[] = values.domains.split(",");
                    await settingsStore.createSingleSignOnURL(domains);
                } catch(e) {
                    throw e;
                } finally {
                    formik.setSubmitting(false);
                }
            };

            type FormProps = {
                domains: string;
            }

            const SSOSchema = Yup.object().shape({
                domains: Yup.string().required('At least one domain is required')
            });

            const initialValues: FormProps = useMemo(() => {
                return {
                    domains: 'example.com'
                };
            }, []);


            function createURL() : ReactElement {
                return  <Formik initialValues={initialValues} validationSchema={SSOSchema} onSubmit={onFormSubmit}>
                    {(formik: FormikProps<FormProps>) => (
                        <SP.DialogBody>
                            <S.SubCategoryItemRow>
                                <SP.DescriptionLabel>Create an SSO Configuration URL by specifying which email domains will be tied to your organization (ex: 'example.com' if an email in your org is 'user@example.com')</SP.DescriptionLabel>
                            </S.SubCategoryItemRow>
                            <SP.InputGroup>
                                <SP.Label>Domain Names (comma-separated)</SP.Label>
                                <SP.FormField name="domains" maxLength={45} component={InputField} />
                            </SP.InputGroup>
                            <ActionsContainer>
                                <S.SubCategoryAddButton variant="primary" type="submit" onClick={formik.submitForm} disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? <SP.Pending size="20px" /> : <><S.SubCategoryPlus path={mdiPlus} size={1} /> Create SSO Configuration URL</>}
                                </S.SubCategoryAddButton>
                            </ActionsContainer>
                        </SP.DialogBody>
                    )}
                </Formik>
            }

            const handleCopyAction = (): void => {
                if(settingsStore.singleSignOnURL) {
                    copy(settingsStore.singleSignOnURL);
                    toastContext.notify && toastContext.notify('URL copied to clipboard', 'left');
                }
            };

            function displayURL() : ReactElement {
                return <SP.DialogBody>
                    <S.SubCategoryItemRow>
                        <SP.DescriptionLabel>To setup Single Sign-On, please give the following URL to your IT specialist in order to continue setup:</SP.DescriptionLabel>
                    </S.SubCategoryItemRow>
                    <SP.InputGroup>
                        <SP.Label>URL:</SP.Label>
                        <S.SubCategoryItemRow>
                            <SP.DescriptiveField value={settingsStore.singleSignOnURL} disabled={true} />
                            <SP.ActionButton onClick={handleCopyAction}>
                                <SP.ActionTooltip direction="top" variant="dark" title={<span>Copy URL</span>}>
                                    <SP.ActionIcon path={mdiClipboardArrowUpOutline} size={0.75} />
                                </SP.ActionTooltip>
                            </SP.ActionButton>
                        </S.SubCategoryItemRow>
                    </SP.InputGroup>
                </SP.DialogBody>;
            }



            return <S.SubCategoryCol>
                    <S.SubCategoryItem key="members">
                        <S.SubCategoryItemRow>
                            <S.SubCategoryH1>
                                {isTabletPortraitDown() && (
                                    <S.ReturnLink to="/settings">
                                        {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                    </S.ReturnLink>
                                )}
                                Single Sign-On Integration
                            </S.SubCategoryH1>

                            {settingsStore.singleSignOnURL ? displayURL() : createURL()}

                            {/*<S.SubCategoryAddButton variant="primary" onClick={onClickAddKey}>*/}
                            {/*    <S.SubCategoryPlus path={mdiPlus} size={1} /> Add new API Key*/}
                            {/*</S.SubCategoryAddButton>*/}

                        </S.SubCategoryItemRow>
                    </S.SubCategoryItem>
                </S.SubCategoryCol>;
        },
    ),
);